import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const FuturisticDataViz = ({
  data = [],
  type = 'bar',
  color = '#00D4FF',
  className = '',
  animate = true,
  interactive = true
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animatedData, setAnimatedData] = useState([]);

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => {
        setAnimatedData(data);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setAnimatedData(data);
    }
  }, [data, animate]);

  const maxValue = Math.max(...data.map(d => d.value));

  const BarChart = () => (
    <div className="flex items-end justify-between h-40 space-x-2">
      {animatedData.map((item, index) => (
        <motion.div
          key={index}
          className="flex flex-col items-center space-y-2 flex-1"
          onMouseEnter={() => interactive && setHoveredIndex(index)}
          onMouseLeave={() => interactive && setHoveredIndex(null)}
        >
          <motion.div
            className="w-full rounded-t-lg relative overflow-hidden"
            style={{ backgroundColor: color }}
            initial={{ height: 0 }}
            animate={{ height: `${(item.value / maxValue) * 100}%` }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            whileHover={interactive ? { scale: 1.02 } : {}}
          >
            {/* Glow effect */}
            <div
              className="absolute inset-0 opacity-30 blur-sm"
              style={{ backgroundColor: color }}
            />

            {/* Value display on hover */}
            {hoveredIndex === index && (
              <motion.div
                className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {item.value}
              </motion.div>
            )}
          </motion.div>

          <span className="text-xs text-white/70 text-center">
            {item.label}
          </span>
        </motion.div>
      ))}
    </div>
  );

  const CircularProgress = () => (
    <div className="grid grid-cols-2 gap-6">
      {animatedData.map((item, index) => {
        const percentage = (item.value / maxValue) * 100;
        const circumference = 2 * Math.PI * 40;
        // The strokeDasharray should be the full circumference to start,
        // and strokeDashoffset will animate to reveal the progress.
        const strokeDasharray = circumference;
        const strokeDashoffset = circumference - (percentage / 100) * circumference;

        return (
          <motion.div
            key={index}
            className="flex flex-col items-center space-y-2"
            onMouseEnter={() => interactive && setHoveredIndex(index)}
            onMouseLeave={() => interactive && setHoveredIndex(null)}
          >
            <div className="relative w-24 h-24">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="8"
                  fill="none"
                />

                {/* Glow circle - This is the new element for the glow */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke={color}
                  strokeWidth="10" // Slightly thicker for a softer glow
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDasharray, strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset }}
                  transition={{ duration: 1.5, delay: index * 0.2 }}
                  style={{
                    filter: 'blur(4px)', // Apply blur to create the glow
                    opacity: 0.6 // Adjust opacity for desired glow intensity
                  }}
                />

                {/* Progress circle (main, on top of glow) */}
                <motion.circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke={color}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDasharray, strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset }}
                  transition={{ duration: 1.5, delay: index * 0.2 }}
                  // Removed filter: drop-shadow from here
                />
              </svg>

              {/* Center value */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {Math.round(percentage)}%
                </span>
              </div>
            </div>

            <span className="text-xs text-white/70 text-center">
              {item.label}
            </span>

            {hoveredIndex === index && (
              <motion.div
                className="text-xs text-brand-cyan font-semibold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {item.value}
              </motion.div>
            )}
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className={`p-6 ${className}`}>
      {type === 'bar' ? <BarChart /> : <CircularProgress />}
    </div>
  );
};

export default FuturisticDataViz;